/**
 * Catalog of process duration behavior types and artifact generation modes
 */

export interface DurationBehaviorOption {
  value: 'fixed_period' | 'capacity_driven' | 'event_driven' | 'continuous';
  label: string;
  description: string;
  useCases: string[];
  configFields?: string[];  // Which config fields are relevant
}

export interface GenerationModeOption {
  value: 'singleton' | 'sequential' | 'concurrent';
  label: string;
  description: string;
  examples: string[];
  configFields?: string[];
}

export const DURATION_BEHAVIOR_CATALOG: DurationBehaviorOption[] = [
  {
    value: 'fixed_period',
    label: 'Fixed Period',
    description: 'Process runs for a fixed time period (e.g., monthly accounting cycle)',
    useCases: ['revenue', 'expenses', 'financial_reporting', 'inventory_count'],
    configFields: ['period_type', 'period_calendar_ref'],
  },
  {
    value: 'capacity_driven',
    label: 'Capacity Driven',
    description: 'Process duration determined by resource capacity and throughput',
    useCases: ['manufacturing', 'service_delivery', 'fulfillment'],
    configFields: ['resource_ref', 'capacity_constraint_field'],
  },
  {
    value: 'event_driven',
    label: 'Event Driven',
    description: 'Process runs from start event to end event (e.g., invoice created → sent)',
    useCases: ['invoice', 'order_fulfillment', 'project_delivery'],
    configFields: ['start_event', 'end_event'],
  },
  {
    value: 'continuous',
    label: 'Continuous',
    description: 'Process is always running (e.g., customer support)',
    useCases: ['monitoring', 'support', 'maintenance'],
    configFields: [],
  },
];

export const ARTIFACT_GENERATION_MODE_CATALOG: GenerationModeOption[] = [
  {
    value: 'singleton',
    label: 'Singleton',
    description: 'Only ONE artifact item can exist at a time (must be deleted/archived before creating next)',
    examples: [
      'Business Entity (only one active entity)',
      'Current Fiscal Year (only one year active at a time)',
      'Active Configuration (only one active config version)',
    ],
    configFields: ['replacement_behavior'],
  },
  {
    value: 'sequential',
    label: 'Sequential',
    description: 'One artifact item at a time, but can create next when current completes/closes',
    examples: [
      'Accounting Periods (March closes before April opens)',
      'Quarterly Reports (Q1 must complete before Q2 starts)',
      'Monthly Revenue Lanes (one per month, sequential)',
    ],
    configFields: ['completion_required', 'max_active_items'],
  },
  {
    value: 'concurrent',
    label: 'Concurrent',
    description: 'Many artifact items can exist and be created simultaneously (default for most processes)',
    examples: [
      'Invoices (multiple invoices at once)',
      'Customer Orders (many orders active)',
      'Revenue Transactions (concurrent transactions)',
      'Inventory Items (many items simultaneously)',
    ],
    configFields: ['max_total_items'],
  },
];

/**
 * Get duration behavior option by value
 */
export function getDurationBehaviorOption(value: string): DurationBehaviorOption | undefined {
  return DURATION_BEHAVIOR_CATALOG.find(opt => opt.value === value);
}

/**
 * Get generation mode option by value
 */
export function getGenerationModeOption(value: string): GenerationModeOption | undefined {
  return ARTIFACT_GENERATION_MODE_CATALOG.find(opt => opt.value === value);
}

/**
 * Get duration behaviors for a use case
 */
export function getDurationBehaviorsForUseCase(useCase: string): DurationBehaviorOption[] {
  return DURATION_BEHAVIOR_CATALOG.filter(opt => opt.useCases.includes(useCase));
}

